<script setup>
import {ref, onMounted, computed} from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import BasePage from "@/components/Layouts/BasePage.vue";
import { POPEntity } from "@/entities/POPEntity";
import {getPOPs, deletePOP} from "@/services/api/POP.js";
import {handleError} from "@/utils/errors.js";
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";
import BaseAdvancedFilter from "@/components/common/Filters/BaseAdvancedFilter.vue";
import {getAvailableActionsForRoute} from "@/services/api/permissionManagement.js";
import {computeClass} from "@/utils/styleFormats.js";
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import BaseTable from "@/components/tables/BaseTable.vue";

const { t } = useI18n();
const router = useRouter();
const entity = new POPEntity();

const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const filters = ref([]);
const quickSearchConfig = computed(() => entity.pages.list.filters.quickSearch);
const actions = ref([]);
const havePermission = ref(false);
const showDialogConfirmDelet = ref(false);
const itemToDelete = ref(null)
const orderBy = ref(null);

async function fetchData(params = {}) {
    try {
        loading.value = true;
        if (orderBy.value) {
            params['orderBy'] = orderBy.value.key;
        }
        currentPage.value = params.page ?? currentPage.value;
        if (searchText.value) {
            const field = quickSearchConfig.value.searchBy[0];
            params[field] = "%" + searchText.value + "%";
        }

        if (filters.value && Object.keys(filters.value).length > 0) {
            params = {
                ...filters.value,
                ...params
            };
        }

        const response = await getPOPs({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...params
        });

        items.value = response.data.items ?? response.data;
        totalItems.value = response.page;
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

function onEdit(item) {
    router.push(`${entity.menuPath}/pop/edit/${item.id}`);
}

async function onPageChange(page) {
    await fetchData({page: page});
}

async function onDelete(item) {
    itemToDelete.value = item;
    showDialogConfirmDelet.value = true;
}

async function onAdvancedFilter(filtersAdvanced) {
    filters.value = filtersAdvanced;
    await fetchData(filtersAdvanced);
}

async function onOrderBy(orderByArray) {
    orderBy.value = orderByArray;
}

async function onCleanFilter() {
    filters.value = null;
    await fetchData();
}

const confirmDelete = async () => {
    try {
        await deletePOP({id: itemToDelete.value.id});
        await fetchData();
        message.success(t('common.messages.success.itemDeletedSuccessfully'));
        showDialogConfirmDelet.value = false;
    } catch (error) {
        message.error(await handleError(error, t));
    }
};

onMounted(async () => {
    actions.value = await getAvailableActionsForRoute("Resources/ISP/POP");

    if (!actions.value?.list?.length) {
        message.error(t("common.messages.errors.403"));
    } else {
        havePermission.value = true;
        await fetchData();
    }
})
</script>

<template>
    <BasePage :entity="entity">
        <template #default>
            <v-row class="mb-4">
                <v-col>
                    <ButtonNew  route="pop/create"/>
                </v-col>
                <v-col class="base-advanced-filter">
                    <BaseAdvancedFilter
                        :entity="entity"
                        @filter="onAdvancedFilter"
                        @clean="onCleanFilter"
                    />
                </v-col>
                <v-col class="base-quick-search">
                    <BaseQuickSearch
                        :entity="entity"
                        v-if="quickSearchConfig"
                        :config="quickSearchConfig"
                        v-model="searchText"
                        :fetchData="fetchData"
                    />
                </v-col>
            </v-row>

            <BaseTable
                :fetchData="fetchData"
                :entity="entity"
                :items="items"
                :loading="loading"
                :totalItems="totalItems"
                :currentPage="currentPage"
                :itemsPerPage="itemsPerPage"
                :actions="actions"
                @edit="onEdit"
                @delete="onDelete"
                @page-change="onPageChange"
                @onOrderBy="onOrderBy"
            >
                <template #status="{ row }">
                    <span class="trezo-status-badge" :class="computeClass(row.status)" >
                        {{ t('common.labels.' + (row.status ? 'active' : 'inactive' )) }}
                    </span>
                </template>
            </BaseTable>

            <DialogConfirmDelet
                v-model="showDialogConfirmDelet"
                :item="itemToDelete?.name"
                @confirm="confirmDelete"
            />

        </template>
    </BasePage>
</template>
